package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.core.util.PeriodSelection
import br.com.vvrefeicoes.portal.domain.model.ComplaintsSnapshot
import br.com.vvrefeicoes.portal.domain.model.CountItem
import br.com.vvrefeicoes.portal.domain.model.MonthCount
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.ComplaintsDashboardRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.statement.bodyAsText
import io.ktor.http.HttpHeaders
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class RemoteComplaintsDashboardRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : ComplaintsDashboardRepository {
    override suspend fun load(
        selection: PeriodSelection,
        unitId: String?,
        manager: String?,
        director: String?,
    ): ComplaintsSnapshot {
        val path = "/operational/complaints?${panelQuery(selection, unitId, manager, director)}"
        val response = httpClient.get(baseUrl + path) { authorize() }
        if (!response.status.isSuccess()) {
            val message =
                runCatching {
                    Json { ignoreUnknownKeys = true }
                        .decodeFromString<PanelApiErrorDto>(response.bodyAsText())
                        .message
                }.getOrNull()
            error(message ?: "HTTP ${response.status.value}")
        }
        return response.body<ComplaintsDto>().toDomain()
    }

    private fun io.ktor.client.request.HttpRequestBuilder.authorize() {
        authRepository.session.value?.token?.let { token ->
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }
}

@Serializable
private data class ComplaintsDto(
    val periodLabel: String = "",
    val months: List<PanelMonthDto> = emptyList(),
    val total: Long = 0,
    val byStatus: Map<String, Long> = emptyMap(),
    val evolution: List<MonthCountDto> = emptyList(),
    val byShift: List<LabelCountDto> = emptyList(),
    val byService: List<LabelCountDto> = emptyList(),
    val byNature: List<LabelCountDto> = emptyList(),
    val byUnit: List<LabelCountDto> = emptyList(),
    val items: List<CustomerComplaintDto> = emptyList(),
)

@Serializable
private data class MonthCountDto(
    val period: PanelMonthDto = PanelMonthDto(),
    val count: Long = 0,
)

@Serializable
private data class LabelCountDto(
    val label: String = "",
    val count: Long = 0,
)

private fun ComplaintsDto.toDomain(): ComplaintsSnapshot =
    ComplaintsSnapshot(
        periodLabel = periodLabel,
        months = months.map { MonthPeriod(it.year, it.month) },
        total = total.toInt(),
        byStatus = byStatus.mapValues { it.value.toInt() },
        evolution = evolution.map { MonthCount(MonthPeriod(it.period.year, it.period.month), it.count.toInt()) },
        byShift = byShift.map { CountItem(it.label, it.count.toInt()) },
        byService = byService.map { CountItem(it.label, it.count.toInt()) },
        byNature = byNature.map { CountItem(it.label, it.count.toInt()) },
        byUnit = byUnit.map { CountItem(it.label, it.count.toInt()) },
        records = items.map { it.toDomain() },
    )
